import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../shared/components/Sidebar';
import '../../shared/styles/Sidebar.css';
import { useTheme } from '../../shared/context/ThemeContext';

import VerbsBaseMode from './modes/verbs/BaseMode';
import MultipleChoiceVerbs from './modes/verbs/MultipleChoiceVerbs';
import VerbWordList from './modes/verbs/WordList';

import NounsMode from './modes/nouns/NounsMode';
import NounsWordList from './modes/nouns/NounsWordList';
import MultipleChoiceNouns from './modes/nouns/MultipleChoiceNouns';

import GenericQuizMode from './modes/generic/GenericQuizMode';
import GenericMultipleChoiceMode from './modes/generic/GenericMultipleChoiceMode';
import GenericWordList from './modes/generic/GenericWordList';

const CATEGORIES = [
    {
        header: 'Werkwoorden',
        gradient: { primary: '#c97c10', secondary: '#f0a500' },
        modes: [
            { key: 'baseVerbs',           label: 'Werkwoorden' },
            { key: 'multipleChoiceVerbs', label: 'Werkwoorden MC' },
            { key: 'verbList',            label: 'Woordenlijst' },
        ],
    },
    {
        header: 'Zelfstandige naamwoorden',
        gradient: { primary: '#0e8c8c', secondary: '#14b8b8' },
        endpoint: 'nouns',
        accentColor: '#14b8b8',
        accentColor2: '#0e8c8c',
        hoverColor: 'rgba(20,184,184,0.05)',
        modes: [
            { key: 'nouns',               label: 'Zelfstandige naamwoorden' },
            { key: 'multipleChoiceNouns', label: 'Zelfstandige naamwoorden MC' },
            { key: 'nounList',            label: 'Woordenlijst' },
        ],
    },
    {
        header: 'Bijvoeglijke naamwoorden',
        gradient: { primary: '#b8186e', secondary: '#e0308e' },
        endpoint: 'adjectives',
        accentColor: '#e0308e',
        accentColor2: '#b8186e',
        hoverColor: 'rgba(224,48,142,0.06)',
        modes: [
            { key: 'adjectives',               label: 'Bijvoeglijke naamwoorden' },
            { key: 'multipleChoiceAdjectives', label: 'Bijvoeglijke naamwoorden MC' },
            { key: 'adjectivesList',           label: 'Woordenlijst' },
        ],
    },
    {
        header: 'Bijwoorden',
        gradient: { primary: '#1560c8', secondary: '#2e80f0' },
        endpoint: 'adverbs',
        accentColor: '#f59e0b',
        accentColor2: '#d97706',
        hoverColor: 'rgba(245,158,11,0.06)',
        modes: [
            { key: 'adverbs',               label: 'Bijwoorden' },
            { key: 'multipleChoiceAdverbs', label: 'Bijwoorden MC' },
            { key: 'adverbsList',           label: 'Woordenlijst' },
        ],
    },
    {
        header: 'Voornaamwoorden',
        gradient: { primary: '#1a8c48', secondary: '#26b85e' },
        endpoint: 'pronouns',
        accentColor: '#26b85e',
        accentColor2: '#1a8c48',
        hoverColor: 'rgba(38,184,94,0.06)',
        modes: [
            { key: 'pronouns',               label: 'Voornaamwoorden' },
            { key: 'multipleChoicePronouns', label: 'Voornaamwoorden MC' },
            { key: 'pronounsList',           label: 'Woordenlijst' },
        ],
    },
    {
        header: 'Voorzetsels',
        gradient: { primary: '#6230c0', secondary: '#8050e0' },
        endpoint: 'prepositions',
        accentColor: '#8050e0',
        accentColor2: '#6230c0',
        hoverColor: 'rgba(128,80,224,0.06)',
        modes: [
            { key: 'prepositions',               label: 'Voorzetsels' },
            { key: 'multipleChoicePrepositions', label: 'Voorzetsels MC' },
            { key: 'prepositionsList',           label: 'Woordenlijst' },
        ],
    },
];

// Theme-aware tokens
function getTokens(isDark) {
    return {
        pageBg: isDark
            ? 'linear-gradient(135deg, #0d0d1a 0%, #0f0f23 35%, #0a1628 70%, #0d0d1a 100%)'
            : 'linear-gradient(135deg, #dddaf0 0%, #e0ddf2 35%, #d8e0f0 70%, #dddaf0 100%)',
        modeActiveBg: isDark ? '#0a0a0f' : '#d5d2e8',
        cardBg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.45)',
        cardBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(80,70,140,0.15)',
        cardShadow: isDark
            ? 'inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 24px rgba(0,0,0,0.3)'
            : 'inset 0 1px 0 rgba(255,255,255,0.8), 0 4px 24px rgba(80,70,140,0.12)',
        sectionLabel: isDark ? '#6060a0' : '#5548c0',
        btnBg: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.5)',
        btnBorder: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(80,70,140,0.2)',
        btnBgHover: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.7)',
        btnBorderHover: isDark ? 'rgba(255,255,255,0.22)' : 'rgba(80,70,140,0.4)',
        btnShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.07)' : 'inset 0 1px 0 rgba(255,255,255,0.9)',
        btnShadowHover: isDark
            ? 'inset 0 1px 0 rgba(255,255,255,0.12), 0 8px 24px rgba(0,0,0,0.4)'
            : 'inset 0 1px 0 rgba(255,255,255,0.9), 0 8px 24px rgba(80,70,140,0.2)',
        btnText: isDark ? '#e8e8f0' : '#1a1830',
        accentBtnBg: isDark ? 'rgba(108,99,255,0.18)' : 'rgba(85,72,224,0.15)',
        accentBtnBorder: isDark ? 'rgba(108,99,255,0.35)' : 'rgba(85,72,224,0.35)',
        accentBtnText: isDark ? '#b0aaff' : '#3a2ec0',
        accentBtnHoverBg: isDark ? 'rgba(108,99,255,0.28)' : 'rgba(85,72,224,0.28)',
        accentBtnHoverBorder: isDark ? 'rgba(108,99,255,0.6)' : 'rgba(85,72,224,0.6)',
        accentBtnHoverText: isDark ? '#d0ccff' : '#2a1eb0',
        titleGradient: isDark
            ? 'linear-gradient(135deg, #fff 0%, #9d97ff 60%, #7878a0 100%)'
            : 'linear-gradient(135deg, #1a1830 0%, #4a40c0 60%, #6060a0 100%)',
        subtitle: isDark ? '#7878a0' : '#5050a0',
        glowColor1: isDark ? 'rgba(108,99,255,0.13)' : 'rgba(108,99,255,0.08)',
        glowColor2: isDark ? 'rgba(37,117,252,0.10)' : 'rgba(37,117,252,0.06)',
    };
}

export default function DarijaPage() {
    const [mode, setMode] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const navigate = useNavigate();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const t = getTokens(isDark);
    const goBack = () => setMode(null);

    if (mode) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', background: t.modeActiveBg, overflow: 'hidden' }}>
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', zIndex: 1 }}>
                    {renderMode(mode, goBack)}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                px: { xs: 2, sm: 4 },
                py: { xs: 6, sm: 8 },
                background: t.pageBg,
                overflow: 'hidden',
                position: 'relative',
                '&::before': ambientGlow('top', '-15%', 'right', '-5%', t.glowColor1),
                '&::after':  ambientGlow('bottom', '-15%', 'left', '-5%', t.glowColor2),
            }}
        >
            {!isSidebarOpen && (
                <button className="menu-btn" onClick={() => setIsSidebarOpen(true)}>☰</button>
            )}
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

            <Box sx={{ width: '100%', maxWidth: 780, mb: { xs: 4, sm: 6 }, position: 'relative', zIndex: 1 }}>
                <Typography
                    variant="h3"
                    sx={{
                        fontFamily: "'Syne', sans-serif",
                        fontWeight: 800,
                        fontSize: { xs: '2rem', sm: '2.8rem' },
                        letterSpacing: '-0.03em',
                        background: t.titleGradient,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        mb: 1,
                    }}
                >
                    Darija Leren
                </Typography>
                <Typography sx={{ color: t.subtitle, fontFamily: "'DM Sans', sans-serif", fontSize: '0.95rem' }}>
                    Kies een categorie en modus om te beginnen
                </Typography>
            </Box>

            <Box sx={{ width: '100%', maxWidth: 780, position: 'relative', zIndex: 1 }}>
                {CATEGORIES.map((cat) => (
                    <CategorySection key={cat.header} cat={cat} onSelect={setMode} tokens={t} />
                ))}
            </Box>

            <Box sx={{ position: 'relative', zIndex: 1, mt: 2 }}>
                <Button
                    variant="contained"
                    onClick={() => navigate('/Update')}
                    sx={{
                        borderRadius: '12px',
                        background: t.accentBtnBg,
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        border: `1px solid ${t.accentBtnBorder}`,
                        color: t.accentBtnText,
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 3,
                        py: 1,
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1)',
                        '&:hover': {
                            background: t.accentBtnHoverBg,
                            borderColor: t.accentBtnHoverBorder,
                            color: t.accentBtnHoverText,
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.15), 0 4px 20px rgba(108,99,255,0.25)',
                        },
                    }}
                >
                    Update Woorden Catalogus
                </Button>
            </Box>
        </Box>
    );
}

function CategorySection({ cat, onSelect, tokens: t }) {
    return (
        <Box
            sx={{
                mb: { xs: 3, sm: 4 },
                background: t.cardBg,
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: `1px solid ${t.cardBorder}`,
                borderRadius: '18px',
                padding: { xs: '20px', sm: '28px' },
                boxShadow: t.cardShadow,
            }}
        >
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: t.sectionLabel, mb: 2 }}>
                {cat.header}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 1.5, sm: 2 } }}>
                {cat.modes.map((m, i) => (
                    <ModeButton key={m.key} label={m.label} gradient={buildGradient(cat.gradient, i)} index={i} onClick={() => onSelect(m.key)} tokens={t} />
                ))}
            </Box>
        </Box>
    );
}

function ModeButton({ label, gradient, index, onClick, tokens: t }) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08, duration: 0.35, ease: 'easeOut' }}>
            <Button
                onClick={onClick}
                sx={{
                    width: '100%',
                    height: { xs: 70, sm: 80 },
                    borderRadius: '12px',
                    fontSize: { xs: '0.85rem', sm: '0.92rem' },
                    fontWeight: 600,
                    textTransform: 'none',
                    fontFamily: "'DM Sans', sans-serif",
                    color: t.btnText,
                    background: t.btnBg,
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    border: `1px solid ${t.btnBorder}`,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'border-color 0.2s, transform 0.15s, box-shadow 0.2s, background 0.2s',
                    boxShadow: t.btnShadow,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: gradient,
                        opacity: 0.22,
                        transition: 'opacity 0.2s',
                    },
                    '&:hover': {
                        background: t.btnBgHover,
                        borderColor: t.btnBorderHover,
                        transform: 'translateY(-2px)',
                        boxShadow: t.btnShadowHover,
                        '&::before': { opacity: 0.38 },
                    },
                }}
            >
                {label}
            </Button>
        </motion.div>
    );
}

function renderMode(mode, onBack) {
    if (mode === 'baseVerbs')           return <VerbsBaseMode onBack={onBack} />;
    if (mode === 'multipleChoiceVerbs') return <MultipleChoiceVerbs onBack={onBack} />;
    if (mode === 'verbList')            return <VerbWordList onBack={onBack} />;
    if (mode === 'nouns')               return <NounsMode onBack={onBack} />;
    if (mode === 'nounList')            return <NounsWordList onBack={onBack} />;
    if (mode === 'multipleChoiceNouns') return <MultipleChoiceNouns onBack={onBack} />;

    const cat = CATEGORIES.find((c) => c.modes.some((m) => m.key === mode));
    if (!cat || !cat.endpoint) return null;
    const modeConfig = cat.modes.find((m) => m.key === mode);

    if (mode.startsWith('multipleChoice')) return <GenericMultipleChoiceMode endpoint={cat.endpoint} label={modeConfig.label} onBack={onBack} />;
    if (mode.endsWith('List')) return <GenericWordList endpoint={cat.endpoint} title={cat.header} accentColor={cat.accentColor} accentColor2={cat.accentColor2} hoverColor={cat.hoverColor} onBack={onBack} />;
    return <GenericQuizMode endpoint={cat.endpoint} label={modeConfig.label} onBack={onBack} />;
}

function hexToRgb(hex) {
    const num = parseInt(hex.replace('#', ''), 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function buildGradient({ primary, secondary }, modeIndex) {
    const shade = 1 - modeIndex * 0.15;
    const [r1, g1, b1] = hexToRgb(primary).map((c) => Math.round(c * shade));
    const [r2, g2, b2] = hexToRgb(secondary).map((c) => Math.round(c * shade));
    return `linear-gradient(135deg, rgb(${r1},${g1},${b1}) 0%, rgb(${r2},${g2},${b2}) 100%)`;
}

function ambientGlow(vSide, vPct, hSide, hPct, color) {
    return {
        content: '""',
        position: 'fixed',
        [vSide]: vPct,
        [hSide]: hPct,
        width: '55vw',
        height: '55vw',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${color} 0%, transparent 65%)`,
        pointerEvents: 'none',
        zIndex: 0,
    };
}
